import React from 'react'
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router'

// Conditionally import Sentry only in browser environment
let Sentry: any = null
if (typeof window !== 'undefined') {
	import('@sentry/react')
		.then((sentryModule) => {
			Sentry = sentryModule
		})
		.catch(() => {
			// Sentry not available, that's fine
		})
}

export function init() {
	if (!Sentry) {
		console.warn('Sentry not initialized - Sentry module not available')
		return
	}
	Sentry.init({
		dsn: ENV.SENTRY_DSN,
		environment: ENV.MODE,
		beforeSend(event: any) {
			if (event.request?.url) {
				const url = new URL(event.request.url)
				if (
					url.protocol === 'chrome-extension:' ||
					url.protocol === 'moz-extension:'
				) {
					// This error is from a browser extension, ignore it
					return null
				}
			}
			return event
		},
		integrations: [
			Sentry.replayIntegration(),
			Sentry.browserProfilingIntegration(),
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect: React.useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
		],

		// Set tracesSampleRate to 1.0 to capture 100%
		// of transactions for performance monitoring.
		// We recommend adjusting this value in production
		tracesSampleRate: 1.0,

		// Capture Replay for 10% of all sessions,
		// plus for 100% of sessions with an error
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
	})
}

export function identifySentryClientUser(userId: string) {
	if (Sentry) {
		Sentry.setUser({ id: userId })
	}
}

export function clearSentryClientUser() {
	if (Sentry) {
		Sentry.setUser(null)
	}
}

export function setSentryTag(key: string, value: string) {
	if (Sentry) {
		Sentry.setTag(key, value)
	}
}

export function setSentryTags(tags: Record<string, string>) {
	if (Sentry) {
		Object.entries(tags).forEach(([key, value]) => {
			Sentry.setTag(key, value)
		})
	}
}

export function removeSentryTag(key: string) {
	if (Sentry) {
		Sentry.setTag(key, undefined)
	}
}

export function clearSentryTags() {
	if (Sentry) {
		Sentry.setTags({})
	}
}
